import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import RuleTestingFilters from './rule-testing/RuleTestingFilters';
import DataSourceToggle from './rule-testing/DataSourceToggle';
import RuleSelectionPanel from './rule-testing/RuleSelectionPanel';
import TestResultsDisplay from './rule-testing/TestResultsDisplay';
import { useRuleTesting } from '../hooks/useRuleTesting';
import { useRuleSelection } from '../hooks/useRuleSelection';
import { useExpandableSections } from '../hooks/useExpandableSections';

const RuleTestingPage = ({ customerId }) => {
  // Custom hooks
  const {
    customers,
    rulesetTypes,
    extractionJobs,
    rules,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedRulesetTypeId,
    testResults,
    testing,
    inputMode,
    setInputMode,
    jsonInput,
    setJsonInput,
    jsonError,
    setJsonError,
    setSelectedCustomerId,
    setSelectedRulesetTypeId,
    executeTest,
    clearTestResults,
  } = useRuleTesting();

  // Set the customer ID from prop when provided
  useEffect(() => {
    if (customerId && customerId !== selectedCustomerId) {
      setSelectedCustomerId(customerId);
    }
  }, [customerId, selectedCustomerId, setSelectedCustomerId]);

  const {
    selectedRuleIds,
    toggleRule,
    selectAll,
    isAllSelected,
  } = useRuleSelection(rules);

  const {
    toggleSection,
    expandAll,
    isExpanded,
  } = useExpandableSections({
    inputData: false,
    finalOutput: false,
    rules: {}
  });

  // Local UI state
  const [selectedExtractionJobId, setSelectedExtractionJobId] = useState('');

  // Clear test results when filters change
  useEffect(() => {
    clearTestResults();
    setSelectedExtractionJobId('');
  }, [selectedCustomerId, selectedRulesetTypeId]);

  const handleModeChange = (newMode) => {
    setInputMode(newMode);
    setJsonInput('');
    setJsonError(null);
    clearTestResults();
  };

  const handleTestRules = async () => {
    try {
      const results = await executeTest(selectedExtractionJobId, selectedRuleIds);
      
      // Auto-expand all sections
      expandAll('inputData');
      expandAll('finalOutput');
      const ruleIds = results.ruleResults.map(r => r.ruleId);
      expandAll('rules', ruleIds);
    } catch (err) {
      // Error already handled in hook
    }
  };

  const isTestDisabled = testing || selectedRuleIds.length === 0 || (
    inputMode === 'extraction-job' ? !selectedExtractionJobId : !jsonInput.trim()
  );

  return (
    <div className="user-list">
      <div className="user-list-header">
        <h2>Rule Testing</h2>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <div className="mb-4">
        <RuleTestingFilters
          customers={customerId ? [] : customers}
          rulesetTypes={rulesetTypes}
          selectedCustomerId={selectedCustomerId}
          selectedRulesetTypeId={selectedRulesetTypeId}
          onCustomerChange={customerId ? null : ((value) => {
            setSelectedCustomerId(value);
            clearTestResults();
          })}
          onRulesetTypeChange={(value) => {
            setSelectedRulesetTypeId(value);
            clearTestResults();
          }}
          loading={loading}
          hideCustomerFilter={!!customerId}
        />

        <div className="mt-3 mb-3">
          <DataSourceToggle
            mode={inputMode}
            onModeChange={handleModeChange}
            extractionJobs={extractionJobs}
            selectedExtractionJobId={selectedExtractionJobId}
            onExtractionJobChange={(value) => {
              setSelectedExtractionJobId(value);
              clearTestResults();
            }}
            jsonInput={jsonInput}
            onJsonInputChange={setJsonInput}
            jsonError={jsonError}
            loading={loading}
            disabled={testing}
          />
        </div>

        <div className="mb-3">
          <RuleSelectionPanel
            rules={rules}
            selectedRuleIds={selectedRuleIds}
            selectedCustomerId={selectedCustomerId}
            onRuleToggle={toggleRule}
            onSelectAll={selectAll}
            isAllSelected={isAllSelected}
          />
        </div>

        <div className="text-end">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleTestRules}
            disabled={isTestDisabled}
          >
            {testing && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            <FontAwesomeIcon icon={faPlay} className="me-2" />
            {testing ? 'Testing Rules...' : 'Test Rules'}
          </button>
        </div>
      </div>

      <TestResultsDisplay
        testResults={testResults}
        isExpanded={isExpanded}
        onToggleSection={toggleSection}
      />
    </div>
  );
};

RuleTestingPage.propTypes = {
  customerId: PropTypes.string,
};

RuleTestingPage.defaultProps = {
  customerId: null,
};

export default RuleTestingPage;
